'use client';

import AppImage from '@/components/ui/AppImage';
import { useEffect, useRef } from 'react';
import type { Locale } from '../HomeLanding';

const TRUSTED_BUYERS = [
  { name: 'Skretting', full: 'Skretting' },
  { name: 'Cargill', full: 'Cargill' },
  { name: 'BioMar', full: 'BioMar' },
  { name: 'CP Foods', full: 'Charoen Pokphand Foods' },
  { name: 'De Heus', full: 'De Heus' },
  { name: 'Thai Union', full: 'Thai Union' },
  { name: 'Skretting', full: 'Skretting' },
  { name: 'Cargill', full: 'Cargill' },
  { name: 'BioMar', full: 'BioMar' },
  { name: 'CP Foods', full: 'Charoen Pokphand Foods' },
  { name: 'De Heus', full: 'De Heus' },
  { name: 'Thai Union', full: 'Thai Union' },
];

const STATS = [
  { value: '15,000+', label: 'Tons Annual Production' },
  { value: '45+', label: 'Global Feed Mill Partners' },
  { value: '99%', label: 'Purity & Compliance Rate' },
  { value: '15yr', label: 'Industrial Lipid Expertise' },
];

export default function HeroSection({ locale = 'en' }: { locale?: Locale }) {
  const heroRef = useRef<HTMLDivElement>(null);
  const isVietnamese = locale === 'vn';
  const isIndonesian = locale === 'id';
  const isChinese = locale === 'cn';
  const stats = isVietnamese
    ? [
        { value: '15,000+', label: 'Tấn sản lượng mỗi năm' },
        { value: '45+', label: 'Đối tác nhà máy thức ăn' },
        { value: '99%', label: 'Tỷ lệ tinh khiết & tuân thủ' },
        { value: '15yr', label: 'Kinh nghiệm lipid công nghiệp' },
      ]
    : isIndonesian
      ? [
          { value: '15,000+', label: 'Ton produksi per tahun' },
          { value: '45+', label: 'Mitra pabrik pakan global' },
          { value: '99%', label: 'Tingkat kemurnian & kepatuhan' },
          { value: '15yr', label: 'Pengalaman lipid industri' },
        ]
    : isChinese
      ? [
          { value: '15,000+', label: '年产量(吨)' },
          { value: '45+', label: '全球饲料厂伙伴' },
          { value: '99%', label: '纯度与合规率' },
          { value: '15yr', label: '工业脂质经验' },
        ]
    : STATS;

  useEffect(() => {
    const el = heroRef?.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.reveal').forEach((r, i) => {
              setTimeout(() => r.classList.add('active'), i * 100);
            });
          }
        });
      },
      { threshold: 0.05 }
    );
    observer?.observe(el);
    return () => observer?.disconnect();
  }, []);

  return (
    <section
      ref={heroRef}
      className="pt-28 pb-0 bg-canvas relative overflow-hidden"
      aria-label="Indofishoil hero"
    >
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.045]"
        style={{
          backgroundImage:
            'linear-gradient(var(--charcoal) 1px, transparent 1px), linear-gradient(90deg, var(--charcoal) 1px, transparent 1px)',
          backgroundSize: '56px 56px',
        }}
      />
      <div className="absolute -top-24 right-[-8rem] h-[26rem] w-[26rem] rounded-full bg-blue/8 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-32 left-[-10rem] h-[28rem] w-[28rem] rounded-full bg-charcoal/5 blur-3xl pointer-events-none" />

      <div className="max-w-[1280px] mx-auto px-6 md:px-10 relative z-10">
        <div className="reveal mb-8 flex items-center gap-3">
          <span className="inline-block w-6 h-px bg-blue" />
          <span className="font-mono text-[11px] tracking-[0.18em] text-blue uppercase font-medium">
            {isVietnamese
              ? 'Dầu cá thô - Cấp thức ăn - Nguồn bền vững'
              : isIndonesian
            ? 'CRUDE FISH OIL - Feed-Grade - Sumber berkelanjutan'
              : isChinese
                ? '粗鱼油 - 饲料级 - 可持续来源'
                : 'Crude Fish Oil - Feed Grade - Sustainable Source'}
          </span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[1.2fr_0.8fr] gap-12 items-start">
          <div>
            <h1
              className="reveal reveal-delay-1 font-sans font-bold tracking-[-0.045em] leading-[0.93] text-charcoal mb-8 max-w-4xl"
              style={{ fontSize: 'clamp(3rem, 7.5vw, 7.5rem)' }}
            >
              {isVietnamese ? (
                <>
                  <span className="block">Dầu Cá</span>
                  <span className="block text-[0.76em] leading-[0.92] tracking-[-0.055em] text-titanium font-bold">
                    Nguyên Liệu Chất Lượng Cao
                  </span>
                  <span className="block mt-2 text-charcoal font-bold leading-[0.96]">
                    Dành Cho Thức Ăn Chăn Nuôi
                  </span>
                </>
              ) : isIndonesian ? (
                <>
                  <span className="block">Premium</span>
                  <span className="block text-[0.76em] leading-[0.92] tracking-[-0.055em] text-titanium font-bold">
                    Crude Fish Oil
                  </span>
                  <span className="block mt-2 text-charcoal font-bold leading-[0.96]">
                    untuk Nutrisi Pakan Ternak
                  </span>
                </>
              ) : isChinese ? (
                <>
                  <span className="block">优质</span>
                  <span className="block text-[0.76em] leading-[0.92] tracking-[-0.055em] text-titanium font-bold">
                    粗鱼油
                  </span>
                  <span className="block mt-2 text-charcoal font-bold leading-[0.96]">
                    提升动物饲料营养标准
                  </span>
                </>
              ) : (
                <>
                  <span className="block">Premium</span>
                  <span className="block text-[0.76em] leading-[0.92] tracking-[-0.055em] text-titanium font-bold">
                    Crude Fish Oil
                  </span>
                  <span className="block mt-2 text-charcoal font-bold leading-[0.96]">
                    for Animal Feeds
                  </span>
                </>
              )}
            </h1>

            <p className="reveal reveal-delay-2 text-titanium text-lg md:text-xl leading-relaxed max-w-2xl">
              {isVietnamese
                ? 'PT. WWI cung cấp dầu cá nguyên liệu chất lượng cao cho ngành thức ăn chăn nuôi và thủy sản. Sản phẩm được kiểm soát FFA dưới 3%, độ ẩm dưới 1%, và nguyên liệu từ Cá Trích - Cá Ngừ giúp đảm bảo EPA & DHA tối thiểu 25% cho hiệu quả tăng trưởng ổn định.'
                : isIndonesian
                    ? 'PT. WWI menghadirkan Crude Fish Oil premium untuk nutrisi pakan ternak dan akuakultur. Dengan kadar FFA di bawah 3% dan kelembapan di bawah 1%, produk kami menjaga stabilitas minyak, sementara bahan baku Ikan laut memastikan EPA + DHA minimal 25%.'
                : isChinese
                  ? 'PT. WWI提供高品质粗鱼油，面向饲料与水产营养应用。产品将游离脂肪酸（FFA）控制在3%以下，水分低于1%，并采用沙丁鱼和金枪鱼原料，确保EPA与DHA总含量至少25%。'
                : 'Our Fish Oil sets the industry benchmark for Indonesian exports. By maintaining a Free Fatty Acid (FFA) level below 3% and ensuring moisture levels stay under 1%, we guarantee a stable, rancidity-resistant product. Our specialized processing of Sea fish sources ensures a minimum 25% combined EPA and DHA, driving superior growth in shrimp and fish farming'}
            </p>

            <div className="reveal reveal-delay-3 flex flex-col md:flex-row items-start md:items-center gap-4 mt-6">
              <a
                href="#specifications"
                className="inline-flex items-center gap-2.5 px-7 py-3.5 bg-blue text-white font-semibold text-[14px] rounded-full hover:bg-blue-dim transition-colors duration-200 shadow-card"
              >
                {isVietnamese ? 'Tải thông số sản phẩm' : isIndonesian ? 'Dapatkan Paket Data Produk' : isChinese ? '获取产品资料包' : 'Download Product Specs'}
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                  <path d="M8 2v8M4 7l4 4 4-4M2 13h12" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </a>
              <a
                href="#coa-samples"
              className="inline-flex items-center gap-2 px-6 py-3 text-[14px] font-medium text-charcoal hover:text-blue transition-colors duration-200"
              >
                {isVietnamese ? 'Xem mẫu COA' : isIndonesian ? 'Lihat contoh COA' : isChinese ? '查看 COA 样本' : 'View COA Samples'}
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                  <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </a>
            </div>
            {isIndonesian ? (
              <p className="mt-3 text-[13px] text-titanium">
                Spesifikasi, COA, dan dokumen kepatuhan dalam satu berkas.
              </p>
            ) : null}
          </div>

          <div className="reveal reveal-delay-2 lg:justify-self-end w-full max-w-md">
            <div className="mb-5 overflow-hidden rounded-2xl border border-divider bg-surface shadow-card">
              <div className="relative aspect-[4/5] w-full">
                <AppImage
                  src="/assets/images/fish_oil3.jpg"
                  alt={isVietnamese ? 'Chai dầu cá cấp phòng thí nghiệm' : isIndonesian ? 'Botol minyak ikan kelas laboratorium' : isChinese ? '实验室级粗鱼油瓶' : 'Bottle of lab-grade fish oil'}
                  fill
                  priority
                  sizes="(max-width: 1024px) 100vw, 420px"
                  className="object-cover"
                />
              </div>
              <div className="border-t border-divider px-5 py-4">
                <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-blue">
                  {isVietnamese ? 'Hình ảnh sản phẩm' : isIndonesian ? 'Visual produk' : isChinese ? '产品图示' : 'Product Visual'}
                </p>
                <p className="mt-2 text-[13px] leading-relaxed text-titanium">
                  {isVietnamese
                  ? 'Hình ảnh chai mẫu cho nguồn cung dầu cá cấp thức ăn chăn nuôi.'
                    : isIndonesian
                      ? 'Sampel laboratorium pasokan minyak ikan Feed-Grade.'
                    : isChinese
                      ? '用于质量控制的饲料级鱼油实验室样瓶展示。'
                    : 'Lab bottle presentation for fish oil feed grade supply.'}
                </p>
              </div>
            </div>

            <div className="rounded-2xl border border-divider bg-surface/90 p-6 shadow-card backdrop-blur-sm">
              <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-blue mb-4">
                {isVietnamese ? 'Cam kết sản phẩm' : 'Product promise'}
              </p>
              <div className="space-y-4">
                <div className="flex items-start justify-between gap-4 border-b border-divider pb-4">
                  <div>
                    <div className="text-sm font-semibold text-charcoal">
                      {isVietnamese ? 'Hồ sơ được đảm bảo' : isIndonesian ? 'Profil terjamin' : isChinese ? '指标有保证' : 'Guaranteed profile'}
                    </div>
                    <div className="text-[13px] text-titanium leading-relaxed">
                      {isVietnamese
                        ? 'Mục tiêu EPA / DHA phù hợp với thông số của nhà máy thức ăn.'
                        : isIndonesian
                          ? 'Target EPA / DHA selaras dengan spesifikasi pabrik pakan.'
                        : isChinese
                          ? 'EPA / DHA 指标符合饲料厂规格。'
                        : 'EPA / DHA targets aligned to feed mill specification.'}
                    </div>
                  </div>
                  <span className="font-mono text-[12px] text-charcoal">QA</span>
                </div>
                <div className="flex items-start justify-between gap-4 border-b border-divider pb-4">
                  <div>
                    <div className="text-sm font-semibold text-charcoal">
                      {isVietnamese ? 'Khả năng truy xuất lô' : isIndonesian ? 'Pelacakan batch' : isChinese ? '批次可追溯' : 'Batch traceability'}
                    </div>
                    <div className="text-[13px] text-titanium leading-relaxed">
                      {isVietnamese
                        ? 'COA có sẵn cho từng lô, sẵn sàng cho khâu xem xét mua hàng.'
                        : isIndonesian
                          ? 'COA tersedia untuk setiap batch, siap ditinjau tim pembelian.'
                        : isChinese
                          ? '每一批都有 COA，可直接供采购审核。'
                        : 'COA available for every lot, ready for procurement review.'}
                    </div>
                  </div>
                  <span className="font-mono text-[12px] text-charcoal">COA</span>
                </div>
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="text-sm font-semibold text-charcoal">
                      {isVietnamese ? 'Sẵn sàng xuất khẩu' : isIndonesian ? 'Siap ekspor' : isChinese ? '可出口供货' : 'Export-ready supply'}
                    </div>
                    <div className="text-[13px] text-titanium leading-relaxed">
                      {isVietnamese
                        ? 'Nguồn cung công nghiệp ổn định với định vị bền vững.'
                        : isIndonesian
                          ? 'Pasokan industri stabil dengan posisi berkelanjutan.'
                        : isChinese
                          ? '工业供货稳定，并具备可持续定位。'
                        : 'Stable industrial output with sustainability positioning.'}
                    </div>
                  </div>
                  <span className="font-mono text-[12px] text-charcoal">EX</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="reveal reveal-delay-4 grid grid-cols-2 md:grid-cols-4 gap-0 border border-divider rounded-2xl overflow-hidden mb-0 bg-surface/70">
          {stats?.map((stat, i) => (
            <div
              key={stat?.label}
              className={`px-7 py-6 bg-surface flex flex-col gap-1 ${
                i < stats?.length - 1 ? 'border-r border-divider' : ''
              }`}
            >
              <span className="font-mono text-2xl font-bold text-charcoal tracking-tight">
                {stat?.value}
              </span>
              <span className="text-[12px] text-titanium font-medium tracking-wide uppercase">
                {stat?.label}
              </span>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-0 border-t border-b border-divider bg-surface overflow-hidden py-5">
        <div className="max-w-[1280px] mx-auto px-6 md:px-10 mb-3 flex items-center gap-3">
            <span className="text-[11px] font-mono tracking-[0.15em] text-titanium uppercase shrink-0">
            {isVietnamese ? 'Được tin dùng bởi' : isIndonesian ? 'Dipercaya oleh' : isChinese ? '客户信赖' : 'Trusted by'}
          </span>
          <span className="flex-1 h-px bg-divider" />
        </div>
        <div className="overflow-hidden">
          <div className="marquee-track">
            {TRUSTED_BUYERS?.map((logo, i) => (
              <div
                key={`${logo?.name}-${i}`}
                className="shrink-0 mx-10 flex items-center gap-2 group"
                title={logo?.full}
              >
                <div className="w-7 h-7 rounded-full bg-surface-alt border border-divider flex items-center justify-center">
                  <span className="font-mono text-[10px] font-semibold text-titanium tracking-wide">
                    {logo?.name.slice(0, 2).toUpperCase()}
                  </span>
                </div>
                <span className="font-mono text-[12px] font-semibold text-titanium tracking-wide group-hover:text-charcoal transition-colors">
                  {logo?.name}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
